import isEmpty from 'lodash/isEmpty'

import { prepareJsonLdScript } from '@core/helpers'
import { buildProductSchema, getMetas } from '@core/helpers/head'
import { getPlugin as getTranslationPlugin } from '@i18n/utils'
import { ROUTES } from '@router'

import translations from './head.translations'

const getCanonical = ({ $route, $navigation, $store }) => {
  const baseURL = $store.getters['config/baseURL']
  const productRoute = $navigation.resolve(
    {
      name: ROUTES.PRODUCT.HOME,
      params: {
        ...($route.params.locale && { locale: $route.params.locale }),
        slugV2: $route.params.slugV2,
        uuid: $route.params.uuid,
      },
    },
    /**
     * By default, vue-router's resolve method will also use the current route
     * to resolve the route we want. To avoid unexpected results, let's force
     * it to resolve the route with only the params we have
     */
    {},
  )

  return `${baseURL}${productRoute.resolved.path}`
}

const getDescription = ({ $store, product, title }) => {
  const $t = getTranslationPlugin()
  const warrantyDuration = $store.getters['config/warrantyDuration']
  const coolingOffDays = $store.getters['config/coolingOffDays']

  return $t(translations.metaDescription, {
    title,
    warrantyDuration,
    coolingOffDays,
    category: product.trackingDetails.category3Name,
    brand: product.trackingDetails.brand,
    model: product.model,
  })
}

export const head = ({
  $store,
  $route,
  $navigation,
  product,
  ratings,
  price,
  isOutOfStock,
}) => {
  const $t = getTranslationPlugin()

  const image = (product.images[0] || {}).url
  const title = product.rawTitle
  const description = getDescription({ $store, title, product })
  const canonical = getCanonical({ $route, $navigation, $store })

  const isRatesAvailable =
    !isEmpty(ratings) && ratings.averageRate > 0 && ratings.reviewsCount > 0

  const alternateLinks = Object.values(product.links).map(
    ({ href, params: { locale } }) => ({
      rel: 'alternate',
      hreflang: locale,
      href,
    }),
  )

  const productSchema = buildProductSchema({
    title,
    description,
    rate: isRatesAvailable ? ratings : {},
    image,
    brand: product.trackingDetails.brand,
    price,
    isOutOfStock,
  })

  return {
    title: $t(translations.metaTitle, { title }),
    meta: [
      ...getMetas({
        title,
        description,
        image,
        'og:type': 'product',
      }),
    ],
    link: [
      ...alternateLinks,
      {
        href: canonical,
        rel: 'canonical',
      },
    ],
    script: prepareJsonLdScript({ productSchema }),
    __dangerouslyDisableSanitizers: ['script'],
  }
}
